import Appsignal from "@appsignal/javascript"

const READER_FRONTEND_NAMESPACE = "reader-frontend"
const ERROR_TEXT_LIMIT = 500
const MAX_CAUSE_DEPTH = 1

export function startReaderErrorTracking({ key, action, AppsignalClass = Appsignal, target = window }) {
  if (!key) return { disconnect() {}, reportError() {} }

  const appsignal = new AppsignalClass({ key, namespace: READER_FRONTEND_NAMESPACE })
  appsignal.addDecorator((span) => span.setAction(action))

  let connected = true
  const reportError = (value) => {
    if (!connected) return

    const error = asError(value)
    const diagnostics = errorDiagnostics(error)
    appsignal.sendError(error, diagnostics ? (span) => span.setParams({ reader_error: diagnostics }) : undefined)
  }
  const errorHandler = (event) => reportError(event.error || event.message)
  const rejectionHandler = (event) => reportError(event.reason)

  target.addEventListener("error", errorHandler)
  target.addEventListener("unhandledrejection", rejectionHandler)

  return {
    reportError,
    disconnect() {
      connected = false
      target.removeEventListener("error", errorHandler)
      target.removeEventListener("unhandledrejection", rejectionHandler)
    }
  }
}

function asError(value, depth = 0) {
  if (value instanceof Error && depth === 0) return value
  if (!value || typeof value !== "object") return new Error(boundedText(value) || "Unknown reader error")

  const message = boundedText(readProperty(value, "message"))
  const name = boundedString(readProperty(value, "name"))
  const code = boundedValue(readProperty(value, "code"))
  const causeValue = readProperty(value, "cause")
  const cause = depth < MAX_CAUSE_DEPTH && causeValue != null ? asError(causeValue, depth + 1) : undefined
  const error = new Error(message || diagnosticSummary({ name, code, cause }))

  if (name) error.name = name
  if (code !== undefined) error.code = code
  if (cause) error.cause = cause
  return error
}

function readProperty(value, property) {
  try {
    return value[property]
  } catch {
    return undefined
  }
}

function boundedString(value) {
  return typeof value === "string" ? value.slice(0, ERROR_TEXT_LIMIT) : undefined
}

function boundedValue(value) {
  if (typeof value === "string") return value.slice(0, ERROR_TEXT_LIMIT)
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "boolean") return value
}

function boundedText(value) {
  const bounded = boundedValue(value)
  return bounded === undefined ? undefined : String(bounded)
}

function diagnosticSummary({ name, code, cause }) {
  const details = {}
  if (name) details.name = name
  if (code !== undefined) details.code = code
  if (cause) details.cause = cause.message

  const summary = JSON.stringify(details)
  return summary === "{}" ? "Unknown reader error" : summary.slice(0, ERROR_TEXT_LIMIT)
}

function errorDiagnostics(error) {
  const code = boundedValue(readProperty(error, "code"))
  const causeValue = readProperty(error, "cause")
  const details = {}

  if (code !== undefined) details.code = code
  if (causeValue != null) {
    const cause = asError(causeValue, MAX_CAUSE_DEPTH)
    details.cause = {
      name: boundedString(readProperty(cause, "name")) || "Error",
      message: boundedText(readProperty(cause, "message")) || "Unknown reader error"
    }
    const causeCode = boundedValue(readProperty(cause, "code"))
    if (causeCode !== undefined) details.cause.code = causeCode
  }

  return Object.keys(details).length > 0 ? details : undefined
}
